import { useState } from "react";
import styled from "@emotion/styled";
import { palette, typography } from "@jnpmedi/grebob";
import { elementHeight } from "@newComponents/common/Viewer/EConsentDocsPdfViewerPageMarkerZone";
import { defaultMarkerColor, getColorBySignerIndex } from "@newComponents/common/Viewer/types";
import { MvnMessage } from "@newLibs/utils";
import { useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { fadeInBorderMarker } from "@assets/styles/animations";
import { EConsentMarkerProps } from "./EConsentMarker";
import { GovAddressDialog } from "../createICFSidebar/dialogs/GovAddressDialog";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DocumentSignerAttrType } from "@maven-rest/docs/DocumentSign.dto";

export function AddressMarker(props: EConsentMarkerProps) {
  const { markers, setMarkers } = useDocumentMarkerContext();

  const [addressDialogVisibility, setAddressDialogVisibility] = useState<boolean>(false);

  const getMarkerType = (attrType: string) => {
    switch (attrType) {
      case DocumentSignerAttrType.AgentAddressItemMark:
        return {
          index: DocumentSignerAttrType.AgentAddressItemIndex,
          missing: DocumentSignerAttrType.AgentAddressItemMissing,
          value: DocumentSignerAttrType.AgentAddressItemValue,
        };
      default:
        return {
          index: DocumentSignerAttrType.AddressItemIndex,
          missing: DocumentSignerAttrType.AddressItemMissing,
          value: DocumentSignerAttrType.AddressItemValue,
        };
    }
  };
  const markerTypes = getMarkerType(props.marker.attr);

  const addressItemIndex = markers.find((e) => e.attr === markerTypes.index && e.attrUuid === props.marker.groupUuid);
  const addressItemValue = markers.find((e) => e.attr === markerTypes.value && addressItemIndex?.attrUuid === e.groupUuid);
  const addressItemMissing = markers.find((e) => e.attr === markerTypes.missing && addressItemIndex?.attrUuid === e.groupUuid);

  const handleMarkerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (props.isModeSigning) {
      setAddressDialogVisibility(true);
    }

    props.onClick?.(e);
  };

  const handleAddressChange = (newValue: string) => {
    const getMarkerType = (attrType: string) => {
      switch (attrType) {
        case DocumentSignerAttrType.AgentAddressItemMark:
          return {
            index: DocumentSignerAttrType.AgentAddressItemIndex,
            missing: DocumentSignerAttrType.AgentAddressItemMissing,
            value: DocumentSignerAttrType.AgentAddressItemValue,
          };
        default:
          return {
            index: DocumentSignerAttrType.AddressItemIndex,
            missing: DocumentSignerAttrType.AddressItemMissing,
            value: DocumentSignerAttrType.AddressItemValue,
          };
      }
    };
    const markerTypes = getMarkerType(props.marker.attr);

    const addressItemIndex = markers.find((e) => e.attr === markerTypes.index && e.attrUuid === props.marker.groupUuid);
    const addressItemValue = markers.find((e) => e.attr === markerTypes.value && addressItemIndex?.attrUuid === e.groupUuid);

    const updatedMarkers = markers
      .filter(
        (x) => !(x.attr === markerTypes.value && x.attrUuid === addressItemValue?.attrUuid && x.groupUuid === addressItemValue?.groupUuid)
      )
      .concat([
        {
          id: addressItemValue?.id,
          signerUuid: addressItemValue?.signerUuid!,
          target: addressItemValue?.target!,
          signerIndex: addressItemValue?.signerIndex!,
          attr: markerTypes.value,
          attrUuid: addressItemValue?.attrUuid!,
          groupUuid: addressItemValue?.groupUuid!,
          value: newValue,
          page: addressItemValue?.page!,
          x: addressItemValue?.x!,
          y: addressItemValue?.y!,
        },
      ]);
    setMarkers(updatedMarkers);
  };

  return (
    <>
      {addressDialogVisibility && (
        <GovAddressDialog
          open={addressDialogVisibility}
          onClose={() => {
            setAddressDialogVisibility(false);
          }}
          onConfirm={(address) => {
            handleAddressChange(address);
          }}
        />
      )}
      <StyledAddressInputWrapper {...props} isFulfilled={!!addressItemValue?.value} onClick={handleMarkerClick}>
        {props.isModeSigning
          ? addressItemValue?.value ||
            addressItemMissing?.value ||
            MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_ECONSENT_ADDRESS_FIELD_PLACEHOLDER) ||
            "(ADDRESS)"
          : props.children}
      </StyledAddressInputWrapper>
    </>
  );
}

const StyledAddressInputWrapper = styled.div<EConsentMarkerProps & { isFulfilled: boolean }>((props) => ({
  display: props.isHidden ? "none" : "block",
  userSelect: "none",
  boxSizing: "border-box",
  position: "absolute",
  top: props.top,
  left: props.left,
  transform: "translateY(-50%)",
  borderRadius: "2px",
  border: `2px solid ${
    props.isModeSigning ? palette[defaultMarkerColor.additional.border] : palette[getColorBySignerIndex(props.marker.signerIndex).border]
  }`,
  height: elementHeight,
  padding: "1px",

  outline: "none",

  backgroundColor: props.isModeSigning
    ? defaultMarkerColor.additional.background
    : getColorBySignerIndex(props.marker.signerIndex).background,

  color: props.isFulfilled ? palette[defaultMarkerColor.additional.text] : palette["grey040"],
  whiteSpace: "nowrap",
  textIndent: 0,

  ...typography["Content.Subtitle04"],

  cursor: "pointer",

  "&":
    !props.isFulfilled && props.isModeSigning
      ? {
          animation: `${fadeInBorderMarker} 1.6s ease-in-out infinite`,
        }
      : "",

  "&:hover": {
    backgroundColor: props.isModeSigning
      ? defaultMarkerColor.additional.backgroundHover
      : getColorBySignerIndex(props.marker.signerIndex).backgroundHover,
    //NOTE: marker hover 시, UnlinkButton이 보임
    "& > span": {
      opacity: 1,
    },
  },
}));
